import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styles from './AssignedJobsSection.module.css';
import { PRISMAX_DATA_PIPELINE_URL } from '../../../configs/config';
import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import { fmtHrs, fmtUSD, paymentState } from './Jobs/jobsApi';
import { TaskChips, ProgressCell, AmountCell, DueCell } from './Jobs/JobRowCells';
import JobFormModal from './Jobs/JobFormModal';
import JobDeleteModal from './Jobs/JobDeleteModal';
import UnmarkPaymentsModal from './Jobs/UnmarkPaymentsModal';
import JobDetailDrawer from './Jobs/JobDetailDrawer';

// ── Main section ──────────────────────────────────────────────────────────────

const AssignedJobsSection = ({ isAdminMode = false, adminUserId = null }) => {
    const { gatewayToken } = useContext(SwapMainAppContext);

    const [jobs, setJobs] = useState([]);
    const [summary, setSummary] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [selectedJob, setSelectedJob] = useState(null);
    const [formJob, setFormJob] = useState(undefined); // undefined = closed, null = create, object = edit
    const [deletingJob, setDeletingJob] = useState(null);
    const [unmarkingJob, setUnmarkingJob] = useState(null);

    const getHeaders = useCallback(() => {
        if (isAdminMode) {
            const adminToken = localStorage.getItem('prismax_admin_token') || '';
            return { Authorization: `Bearer ${adminToken}` };
        }
        const token = gatewayToken || localStorage.getItem('gatewayToken') || '';
        return { Authorization: `Bearer ${token}` };
    }, [isAdminMode, gatewayToken]);

    const jobsBaseUrl = isAdminMode
        ? `${PRISMAX_DATA_PIPELINE_URL}/data/admin/jobs`
        : `${PRISMAX_DATA_PIPELINE_URL}/data/jobs`;

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const url = isAdminMode ? `${jobsBaseUrl}?user_id=${adminUserId}` : jobsBaseUrl;
            const res = await fetch(url, { headers: getHeaders() });
            if (!res.ok) throw new Error('Failed to load jobs');
            const json = await res.json();
            setJobs(json.data || []);
            setSummary(json.summary || null);
        } catch (e) {
            console.error(e);
            setError('Failed to load assigned jobs.');
        } finally {
            setLoading(false);
        }
    }, [isAdminMode, adminUserId, jobsBaseUrl, getHeaders]);

    const fetchOrders = useCallback(async () => {
        try {
            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/jobs/available-orders`, { headers: getHeaders() });
            if (!res.ok) throw new Error('Failed to load orders');
            const json = await res.json();
            setOrders(json.data || []);
        } catch (e) {
            console.error(e);
        }
    }, [getHeaders]);

    useEffect(() => { fetchJobs(); }, [fetchJobs]);
    useEffect(() => { if (isAdminMode) fetchOrders(); }, [isAdminMode, fetchOrders]);

    const countLabel = useMemo(() => {
        if (!summary || summary.job_count === 0) return '';
        const parts = [
            `${summary.job_count} job${summary.job_count === 1 ? '' : 's'}`,
            `${fmtHrs(summary.hours_logged_total)} / ${fmtHrs(summary.hours_assigned_total)} h`,
            `${fmtUSD(summary.amount_earned_total)} earned`,
        ];
        parts.push(summary.amount_outstanding_total > 0 ? `${fmtUSD(summary.amount_outstanding_total)} outstanding` : 'all settled');
        return parts.join(' · ');
    }, [summary]);

    const handleRowClick = (job) => {
        setSelectedJob((prev) => (prev?.job_id === job.job_id ? null : job));
    };

    const handleMarkPaid = async (e, job) => {
        e.stopPropagation();
        try {
            await markPaid(job);
        } catch (err) {
            console.error(err);
        }
    };

    const markPaid = useCallback(async (job) => {
        const res = await fetch(`${jobsBaseUrl}/${job.job_id}/payments`, { method: 'POST', headers: getHeaders() });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.msg || 'Failed to record payment');
        setJobs((prev) => prev.map((j) => (j.job_id === job.job_id ? json.data : j)));
        setSelectedJob((prev) => (prev && prev.job_id === job.job_id ? json.data : prev));
        setFormJob((prev) => (prev && prev.job_id === job.job_id ? json.data : prev));
        return json.data;
    }, [jobsBaseUrl, getHeaders]);

    // Opens the unmark confirmation modal (from the table row action or from
    // the edit modal's "Not paid" segment — in the latter case, close the
    // edit modal first rather than stacking two overlays).
    const handleRequestUnmark = (job) => {
        setFormJob(undefined);
        setUnmarkingJob(job);
    };

    const voidPayments = useCallback((updatedJob) => {
        setJobs((prev) => prev.map((j) => (j.job_id === updatedJob.job_id ? updatedJob : j)));
        setSelectedJob((prev) => (prev && prev.job_id === updatedJob.job_id ? updatedJob : prev));
        setFormJob((prev) => (prev && prev.job_id === updatedJob.job_id ? updatedJob : prev));
        setUnmarkingJob(null);
    }, []);

    const handleJobSaved = (savedJob) => {
        setJobs((prev) => {
            const exists = prev.some((j) => j.job_id === savedJob.job_id);
            return exists ? prev.map((j) => (j.job_id === savedJob.job_id ? savedJob : j)) : [savedJob, ...prev];
        });
        if (selectedJob?.job_id === savedJob.job_id) setSelectedJob(savedJob);
        setFormJob(undefined);
        fetchJobs();
    };

    const handleJobDeleted = (jobId) => {
        setJobs((prev) => prev.filter((j) => j.job_id !== jobId));
        if (selectedJob?.job_id === jobId) setSelectedJob(null);
        setDeletingJob(null);
        fetchJobs();
    };

    return (
        <div className={styles.section}>
            <div className={styles.tableHeaderRow}>
                <div className={styles.tableLabel}>
                    Assigned jobs
                    {countLabel && <span className={styles.tableLabelSub}>{countLabel}</span>}
                </div>
                {isAdminMode && (
                    <div className={styles.filterRow}>
                        <button className={styles.btnPrimary} onClick={() => setFormJob(null)}>+ New job</button>
                    </div>
                )}
            </div>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.colJobId}>Job ID</th>
                            <th className={styles.colTasks}>Task names</th>
                            <th className={styles.colRate}>Rate</th>
                            <th className={styles.colHours}>Hours logged</th>
                            <th className={styles.colAmount}>Amount</th>
                            <th className={styles.colAssigned}>Assigned date</th>
                            <th className={styles.colDue}>Due date</th>
                            {isAdminMode && <th className={styles.colActions}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {jobs.map((job) => {
                            const isSelected = selectedJob?.job_id === job.job_id;
                            const pay = paymentState(job);
                            return (
                                <tr
                                    key={job.job_id}
                                    className={`${styles.tableRow} ${isSelected ? styles.tableRowSelected : ''}`}
                                    onClick={() => handleRowClick(job)}
                                >
                                    <td>
                                        <span className={styles.jobId}>JOB-{job.job_id}</span>
                                        {isAdminMode && job.order_id && (
                                            <div className={styles.orderBadge}>ORD-{job.order_id}</div>
                                        )}
                                    </td>
                                    <td><TaskChips tasks={job.tasks} /></td>
                                    <td><span className={styles.rateVal}>{fmtUSD(job.rate_usd_per_hour)}</span><span className={styles.rateUnit}>/h</span></td>
                                    <td><ProgressCell job={job} /></td>
                                    <td><AmountCell job={job} /></td>
                                    <td className={styles.dateCell}>{job.created_at.slice(0, 10)}</td>
                                    <td><DueCell dueDate={job.due_date} /></td>
                                    {isAdminMode && (
                                        <td className={styles.actionCell} onClick={(e) => e.stopPropagation()}>
                                            <div className={styles.rowActions}>
                                                <button className={styles.actEdit} onClick={(e) => { e.stopPropagation(); setFormJob(job); }}>Edit</button>
                                                {pay.key !== 'none' && pay.key !== 'paid' && (
                                                    <button className={styles.actPay} onClick={(e) => handleMarkPaid(e, job)}>Mark paid</button>
                                                )}
                                                {pay.key === 'paid' && (
                                                    <button
                                                        className={styles.actPay}
                                                        onClick={(e) => { e.stopPropagation(); setUnmarkingJob(job); }}
                                                    >Unmark</button>
                                                )}
                                                <button className={styles.actDelete} onClick={(e) => { e.stopPropagation(); setDeletingJob(job); }}>Delete</button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                        {!loading && !error && jobs.length === 0 && (
                            <tr>
                                <td colSpan={isAdminMode ? 8 : 7} className={styles.emptyCell}>No jobs assigned yet.</td>
                            </tr>
                        )}
                        {loading && (
                            <tr><td colSpan={isAdminMode ? 8 : 7} className={styles.emptyCell}>Loading…</td></tr>
                        )}
                        {error && (
                            <tr><td colSpan={isAdminMode ? 8 : 7} className={styles.errorCell}>{error}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {isAdminMode && (
                <div className={styles.adminNote}>
                    Admin mode is on — job changes here affect every operator assigned to them.
                </div>
            )}

            {selectedJob && (
                <JobDetailDrawer
                    job={selectedJob}
                    onClose={() => setSelectedJob(null)}
                    getHeaders={getHeaders}
                    jobsBaseUrl={jobsBaseUrl}
                />
            )}

            {formJob !== undefined && (
                <JobFormModal
                    job={formJob}
                    orders={orders}
                    adminUserId={adminUserId}
                    onCancel={() => setFormJob(undefined)}
                    onSaved={handleJobSaved}
                    onMarkPaid={markPaid}
                    onRequestUnmark={handleRequestUnmark}
                    getHeaders={getHeaders}
                    jobsBaseUrl={jobsBaseUrl}
                />
            )}

            {deletingJob && (
                <JobDeleteModal
                    job={deletingJob}
                    onCancel={() => setDeletingJob(null)}
                    onDeleted={handleJobDeleted}
                    getHeaders={getHeaders}
                    jobsBaseUrl={jobsBaseUrl}
                />
            )}

            {unmarkingJob && (
                <UnmarkPaymentsModal
                    job={unmarkingJob}
                    onCancel={() => setUnmarkingJob(null)}
                    onVoided={voidPayments}
                    getHeaders={getHeaders}
                    jobsBaseUrl={jobsBaseUrl}
                />
            )}
        </div>
    );
};

export default AssignedJobsSection;
